import { fetchWithRetry, classifyNetworkError } from './helpers.js';

const ERROR_CODE_TO_I18N_KEY = {
  timeout: 'errorTimeout',
  offline: 'errorOffline',
  rate_limited: 'errorRateLimited',
  auth: 'errorAuth',
  server: 'errorServer'
};

export function resolveErrorMessage(t, code) {
  const key = ERROR_CODE_TO_I18N_KEY[code];
  if (key && typeof t[key] === 'string') return t[key];
  return t.errorCommunication;
}

/**
 * POST a chat message to the webhook-proxy. Returns the raw Response for the
 * caller to inspect (status, body, streaming detection). Throws an error with
 * a `code` property when the HTTP layer rejects the request.
 */
export async function postChatMessage({ apiBaseUrl, webhookUrl, message, botId, userId, signal }) {
  const res = await fetchWithRetry(`${apiBaseUrl}/webhook-proxy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson, application/json'
    },
    body: JSON.stringify({
      webhookUrl,
      message,
      botId,
      user_id: userId,
      stream: true,
      timestamp: new Date().toISOString()
    }),
    signal
  });

  if (!res.ok) {
    const code = classifyNetworkError(null, res);
    const error = new Error(`HTTP ${res.status}`);
    error.code = code;
    error.status = res.status;
    throw error;
  }

  return res;
}

/**
 * Pick a reply string from a non-streaming JSON envelope. Tolerant of various
 * shapes returned by n8n/OpenAI-style adapters.
 */
export function extractReplyFromJson(data) {
  if (!data) return '';
  if (data.response) return data.response;
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0];
    return firstItem.text || firstItem.output || firstItem.content || firstItem.message || '';
  }
  if (typeof data === 'object') {
    const direct = data.text || data.output || data.content || data.message || '';
    if (direct) return direct;
    if (data.choices && data.choices[0]?.message?.content) {
      return data.choices[0].message.content;
    }
  }
  if (typeof data === 'string') return data;
  return '';
}

/**
 * Detect a structured "form" instruction in a non-streaming JSON envelope.
 * The agent (via the n8n `display_lead_form` tool) returns:
 *   { "response": "...", "form": { id, fields: [...], consent? } }
 * Returns the raw form schema or null.
 */
export function extractFormFromJson(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const form = data.form;
  if (!form || typeof form !== 'object' || Array.isArray(form)) return null;
  if (!Array.isArray(form.fields) || form.fields.length === 0) return null;
  return form;
}

/**
 * Pick a `blocks` array out of a non-streaming JSON envelope. The n8n
 * `Build Widget Response` node always emits `{ response, blocks }`, where
 * `blocks` is `[]` when the LLM did not invoke `emit_blocks`. Returns an
 * empty array on any malformed input.
 */
export function extractBlocksFromJson(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  return Array.isArray(data.blocks) ? data.blocks : [];
}

/**
 * POST a structured form submission to the webhook-proxy. The proxy will route
 * it (via `type: "form_submission"`) to the chatbot's n8n workflow, which is
 * expected to bypass the AI agent, persist the lead, and respond with a
 * confirmation message (text). Returns the raw Response.
 */
export async function postFormSubmission({
  apiBaseUrl,
  webhookUrl,
  botId,
  userId,
  sessionId,
  formId,
  formVersion,
  values,
  consent,
  consentLabel,
  signal
}) {
  const payload = {
    type: 'form_submission',
    webhookUrl,
    botId,
    user_id: userId,
    session_id: sessionId,
    formId,
    formVersion,
    data: values,
    consent: consent === true,
    consentLabel: consentLabel || null,
    submittedAt: new Date().toISOString()
  };

  const res = await fetchWithRetry(`${apiBaseUrl}/webhook-proxy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload),
    signal
  });

  if (!res.ok) {
    const code = classifyNetworkError(null, res);
    const error = new Error(`HTTP ${res.status}`);
    error.code = code;
    error.status = res.status;
    throw error;
  }

  return res;
}

// Fire-and-forget lead-form telemetry. Never throws and never blocks the UX.
export function postFormEvent({ apiBaseUrl, botId, userId, sessionId, event, meta }) {
  try {
    const payload = JSON.stringify({
      type: 'form_event',
      botId,
      user_id: userId,
      session_id: sessionId,
      event,
      meta: meta || {}
    });
    const url = `${apiBaseUrl}/lead-form-event`;

    // Prefer fetch + keepalive over sendBeacon. sendBeacon issues a `ping`
    // request, which privacy/ad-blocker extensions routinely drop (showing up
    // as `(blocked:other)`); worse, once the beacon is "accepted" the caller
    // cannot tell it was killed, so the event is silently lost. fetch shows as
    // a normal xhr/fetch request (not blocked — same-host calls like
    // webhook-proxy go through) and `keepalive` lets it survive page unload too.
    // text/plain keeps it a CORS-simple request (no preflight); the edge
    // function parses the body as JSON regardless of content-type.
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: payload,
        keepalive: true,
        mode: 'cors'
      }).catch(() => sendBeaconFallback(url, payload));
      return;
    }

    sendBeaconFallback(url, payload);
  } catch (_) {
    /* telemetry must never break the widget */
  }
}

// Last-resort path for environments without fetch keepalive. Best-effort only.
function sendBeaconFallback(url, payload) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
    }
  } catch (_) {
    /* telemetry is best-effort */
  }
}
